import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CartLinesFromDB } from "../../services/PortfolioModelWalletService";
import { CommandBar, IButtonProps, ICommandBarItemProps, Spinner } from "@fluentui/react";
import { BACKTESTING_DESCRIPTION, DESCRIPTIONS, TTabType } from "./Texts/texts";
import Backtesting, { TRatioAndPeriod } from "./Tabs/Backtesting/Backtesting";
import OmniaServices, { RatioKeys, TDataDiversificacion, TOmniaSimulationResponse, TOptimizarFondosPayload, TRatiosKeyCompletos } from "../../services/OmniaService";
import DiversificacionSimulacion, { TDiversificacionSimulacionData } from "./Tabs/Diversificacion/DiversificacionSimulacion";
import { DataToDivesificationCall } from "./Utils/DataToDiversificationCall";
import { FormatDestinyFundsData, FormatFundsData } from "./Utils/FormatFundsData";
import { FormatToSimulation } from "./Utils/FormatToSimulation";
import { TSelectionState } from "./Tables/TableDestino";
import { TRatiosCarterasData } from "../Portfolios/File/types/CarteraRatioTypes";
import { dataFromSimulationToTable } from "./Utils/FormatDataFromSimulation";

const BASE_CLASS = "simulation";

export type TTableData = TSearchData & {
    originalWeight: number;
    destinyWeight: number;
    posicion_fondo_original: number;
    total_fondos_categoria: number;
    ratiosCompletos: {
        fondo_original: TRatiosKeyCompletos;
        fondo_nuevo: TRatiosKeyCompletos;
    }
};

export type TSearchData = {
    originalIsin: string;
    originalName: string;
    destinyIsin: string;
    destinyName: string;
};

const Simulation = () => {
    const location = useLocation();
    const localTheme = window.localStorage.getItem("theme") || "light";
    const [funds] = React.useState<CartLinesFromDB[]>((location.state as any)?.funds ?? []);
    const [description, setDescription] = useState<string>(BACKTESTING_DESCRIPTION);
    const [cargaDiversificacion, setCargaDiversificacion] = useState<boolean>();
    const [readyDiversificacion, setReadyDiversificacion] = useState<boolean>();
    const [ratioPeriodFlashBackError, setRatioPeriodFlashBackError] = useState<boolean>(true);

    const [diversificacionData, setDiversificacionData] = useState<TDiversificacionSimulacionData>();
    const [simulationData, setSimulationData] = useState<TOmniaSimulationResponse>();
    const [walletsData, setWalletsData] = useState<TRatiosCarterasData[]>([]);

    const [nuevosFunds, setNuevosFunds] = useState<TTableData[]>([]);
    const [payload, setPayload] = useState<TOptimizarFondosPayload>({} as TOptimizarFondosPayload);
    const [ratioAndPeriod, setRatioAndPeriod] = useState<TRatioAndPeriod>({} as TRatioAndPeriod);

    const [menuItems, setMenuItems] = useState<ICommandBarItemProps[]>([]);


    const _overflowItems: ICommandBarItemProps[] = [];
    const overflowProps: IButtonProps = { ariaLabel: 'Más acciones' };

    const [ activeTab, setActiveTab ] = useState<string>('Backtesting'); 

    useEffect(() => {
        const isins: string[] = funds.map(fund => fund.isin);
        setPayload(prev => ({
            ...prev, 
            isins: isins
        }));
    },[funds]);

    useEffect(() => {
        const BAR_TABS: ICommandBarItemProps[] = [
            {
                text: 'Backtesting',
                key: 'backtesting',
                onClick: () => setActiveTab('Backtesting'),
                className: activeTab === 'Backtesting' ? 'is-selected' : '',
            },
            {
                disabled: !readyDiversificacion,
                text: 'Diversificación',
                key: 'diversificacion',
                onClick: () => setActiveTab('DiversificacionSimulacion'),
                className: activeTab === 'DiversificacionSimulacion' ? 'is-selected' : ''
            },
        ];
        setMenuItems(BAR_TABS)
    }, [cargaDiversificacion, diversificacionData, readyDiversificacion])


    const updateDescription = () => {
        setDescription(DESCRIPTIONS[activeTab as TTabType] || '');
    };

    const prepareDiversification = async (fondosData: TSelectionState) => {
        const originalCallData = DataToDivesificationCall({ data: fondosData, isOrigen: true });
        const nuevoCallData = DataToDivesificationCall({ data: fondosData, isOrigen: false });

        const [originalDiversificacionData, nuevoDiversificacionData]: [TDataDiversificacion, TDataDiversificacion] =
            await Promise.all([
                OmniaServices.DiversificacionCartera(originalCallData),
                OmniaServices.DiversificacionCartera(nuevoCallData)
        ]);

        setReadyDiversificacion(true);
        setCargaDiversificacion(false);
        setDiversificacionData({
            original: originalDiversificacionData,
            nuevo: nuevoDiversificacionData
        });
    };

    useEffect(() => {
        updateDescription();
    }, [activeTab]);

    const searchBetterFunds = () => {
        OmniaServices.OptimizarFondos(payload).then(resultado => {
            const formatDestinyFundsdataResult = FormatDestinyFundsData(resultado.resultados, FormatFundsData(funds), payload);
            setNuevosFunds(formatDestinyFundsdataResult);
            setRatioAndPeriod({ 
                ratio: payload.ratio as RatioKeys,
                period: payload.periodo
            });
        })
    };

    const updatePayload = (inputData: string, option: string) => {
        switch (option) {
            case "ratio":
                setPayload(prev => ({ ...prev, ratio: inputData }));
                break;

            case "periodo":
                setPayload(prev => ({ ...prev, periodo: inputData }));
                break;

            case "semanasAtras":
                setPayload(prev => ({ ...prev, semanas_atras: Number(inputData) }));
                break;

            default:
                break;
        }
    };

    useEffect(() => {
        const isError = (payload.periodo !== null && payload.ratio !== null && payload.semanas_atras >= 0 )
        setRatioPeriodFlashBackError(isError);
    }, [payload])

    const simulation = async (selected: TSelectionState) => {
        setReadyDiversificacion(false);
        

        prepareDiversification(selected);
        setCargaDiversificacion(true);
        const payloadSimulation = FormatToSimulation(selected);

        const simulationResponse: TOmniaSimulationResponse = await OmniaServices.SimularFondos(payloadSimulation);

        const originalTableData: TRatiosCarterasData = dataFromSimulationToTable({ratiosHistoricos: simulationResponse.resultados?.ratios_historicos, option: "Original" });

        const nuevoTableData: TRatiosCarterasData = dataFromSimulationToTable({ratiosHistoricos: simulationResponse.resultados?.ratios_historicos, option: "Nuevo" });

        setWalletsData([originalTableData, nuevoTableData]);

        setSimulationData(simulationResponse);

    };

    return (
        <div className={BASE_CLASS}>
            <div className={`${BASE_CLASS}__header`}>
                <div className={`${BASE_CLASS}__header__texts`}>

                    <h2>IronIA Optimizer</h2>
                    <div className={`${BASE_CLASS}__commandBar`}>
                        <CommandBar
                            // @ts-ignore
                            items={menuItems}
                            overflowItems={_overflowItems}
                            overflowButtonProps={overflowProps}
                            ariaLabel="Usa las flechas izquierda y derecha para navegar entre las entradas del menú"
                        />
                        {cargaDiversificacion && <Spinner size={3} style={{ padding: "1em" }} />}
                    </div>
                    <p className={`${BASE_CLASS}__header__description ${localTheme}`}>
                        {description}
                    </p>
                    {cargaDiversificacion && 
                        <div className={`${BASE_CLASS}__loading`}>Se están cargando los datos para acceder a la pestaña de "Diversificación"</div>
                        
                    }
                </div>
            </div>
            <div className={`${BASE_CLASS}__container`}>
                {activeTab.match("Backtesting") &&
                    <Backtesting
                        originalFunds={FormatFundsData(funds)}
                        nuevosFunds={nuevosFunds}
                        simulationInfo={simulationData}
                        tableData={walletsData}
                        searchFunc={async () => searchBetterFunds()}
                        simulationFunc={simulation}
                        ratioPeriod={ratioAndPeriod}
                        payloadFunc={updatePayload}
                        inputsError={ratioPeriodFlashBackError ?? true}
                        />}
                {activeTab.match("DiversificacionSimulacion")
                    && <DiversificacionSimulacion data={diversificacionData ?? {} as TDiversificacionSimulacionData}/>}
            </div>
        </div>
    )
};

export default Simulation;